import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError, LEDGER_KINDS, PERMISSION, can, formatIst, formatMoney, inboxApi, useAuth,
} from '@wa/shared';

/**
 * The balance, and where it went.
 *
 * Two numbers, not one. `available` is what a send is checked against and `held` is
 * money already committed to messages in flight — showing only the balance would
 * have a client wondering why a campaign was refused with money apparently there.
 */
export function LedgerPanel() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [amount, setAmount] = useState('2000');
  const [ordered, setOrdered] = useState<{ gross: number; taxable: number } | null>(null);

  const ledger = useQuery({ queryKey: ['ledger'], queryFn: () => inboxApi.ledger() });
  const statement = useQuery({ queryKey: ['statement'], queryFn: () => inboxApi.statement() });

  const topUp = useMutation({
    mutationFn: (value: number) => inboxApi.topUp(value),
    onSuccess: (order) => {
      setError(null);
      // The order exists; the browser would now open the gateway. Nothing is
      // credited here — the wallet moves when the signed webhook arrives, so a
      // closed tab cannot lose a payment.
      setOrdered({ gross: order.gross, taxable: order.taxable });
      void queryClient.invalidateQueries({ queryKey: ['ledger'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not start that top-up.'),
  });

  const wallet = ledger.data?.wallet;
  const entries = ledger.data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      {wallet && !wallet.isActive && (
        <p role="alert" className="rounded-lg border border-(--color-bad) bg-(--color-note) px-3 py-2.5 text-small">
          <b>Sending is switched off on this account.</b>{' '}
          {wallet.suspensionReason ?? 'Please contact PSM Softtech.'}{' '}
          Replies inside the 24-hour window still go out.
        </p>
      )}

      <section className="grid gap-3 @min-[30rem]:grid-cols-3">
        <Figure label="Available to spend" value={wallet?.available} strong />
        <Figure label="Held for messages in flight" value={wallet?.held}
                hint="Money committed to messages that have been accepted but not yet sent." />
        <Figure label="Balance" value={wallet?.balance}
                hint="Available plus held. This is what the ledger's running total shows." />
      </section>

      {can(user, PERMISSION.settingsWrite) && (
        <section className="rounded-lg border border-(--color-line) bg-(--color-surface) p-3 sm:p-4">
          <h2 className="text-body font-semibold">Add money</h2>
          <p className="mt-1 max-w-prose text-small text-(--color-muted)">
            The amount you enter is what lands in your wallet. GST at 18% is added on top,
            so a {formatMoney(Number(amount) || 0)} top-up is charged as{' '}
            {formatMoney((Number(amount) || 0) * 1.18)}.
          </p>

          <div className="mt-2.5 flex flex-wrap items-end gap-2">
            <label className="flex min-w-32 flex-1 flex-col gap-1">
              <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">
                Wallet credit
              </span>
              <input
                type="number"
                min={100}
                step={100}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
              />
            </label>

            <button
              type="button"
              disabled={topUp.isPending || !(Number(amount) >= 100)}
              onClick={() => topUp.mutate(Number(amount))}
              className="tap rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-40"
            >
              {topUp.isPending ? 'Starting…' : 'Top up'}
            </button>
          </div>

          {ordered && (
            <p role="status" className="mt-2 text-small text-(--color-muted)">
              Order created for {formatMoney(ordered.gross)} — {formatMoney(ordered.taxable)} of credit plus GST.
              Your wallet updates when the payment is confirmed, which happens even if you close this tab.
            </p>
          )}
        </section>
      )}

      {statement.data && (
        <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
          <h2 className="border-b border-(--color-line) px-3 py-2.5 text-body font-semibold sm:px-4">
            This month
          </h2>
          <div className="flex flex-col gap-2 px-3 py-3 text-small sm:px-4">
            <Line label="Opened at" value={statement.data.opening} />
            {statement.data.byCategory.map((row) => (
              <Line
                key={row.category}
                label={`${row.category} · ${row.messages.toLocaleString('en-IN')} messages`}
                value={-row.amount}
              />
            ))}
            <Line label="Added" value={statement.data.totals.added ?? 0} />
            <Line label="Closed at" value={statement.data.closing} strong />
          </div>
        </section>
      )}

      <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
        <h2 className="border-b border-(--color-line) px-3 py-2.5 text-body font-semibold sm:px-4">
          Every line
        </h2>

        {entries.length === 0 ? (
          <p className="px-3 py-6 text-center text-small text-(--color-muted) sm:px-4">
            Nothing yet. Entries appear here as messages go out and payments come in.
          </p>
        ) : (
          <ul className="divide-y divide-(--color-line-dim)">
            {entries.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 sm:px-4">
                <div className="min-w-0 flex-1 basis-40">
                  <p className="truncate text-small">
                    {LEDGER_KINDS[entry.kind] ?? 'Entry'}
                    {entry.templateName && (
                      <span className="text-(--color-muted)"> · {entry.templateName}</span>
                    )}
                  </p>
                  <p className="truncate text-micro text-(--color-muted)">
                    {formatIst(entry.createdAt)}
                    {entry.description && <> · {entry.description}</>}
                  </p>
                </div>

                <span className={[
                  'shrink-0 tabular-nums text-small',
                  entry.kind === 0 || entry.kind === 3 ? 'text-(--color-muted)' : 'text-(--color-good)',
                ].join(' ')}>
                  {entry.kind === 0 || entry.kind === 3 ? '−' : '+'}{formatMoney(entry.amount)}
                </span>

                <span className="w-20 shrink-0 text-right tabular-nums text-micro text-(--color-faint)">
                  {formatMoney(entry.balanceAfter)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Figure({ label, value, hint, strong }: {
  label: string; value: number | undefined; hint?: string; strong?: boolean;
}) {
  return (
    <div className="rounded-lg border border-(--color-line) bg-(--color-surface) px-3 py-2.5">
      <p className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">{label}</p>
      <p className={['mt-0.5 tabular-nums', strong ? 'text-title font-semibold' : 'text-body'].join(' ')}>
        ₹{formatMoney(value)}
      </p>
      {hint && <p className="mt-0.5 text-micro leading-snug text-(--color-muted)">{hint}</p>}
    </div>
  );
}

function Line({ label, value, strong }: { label: string; value: number; strong?: boolean }) {
  return (
    <p className={['flex items-baseline justify-between gap-3', strong ? 'font-semibold' : ''].join(' ')}>
      <span className="min-w-0 truncate text-(--color-ink-soft)">{label}</span>
      <span className="shrink-0 tabular-nums">₹{formatMoney(Math.abs(value))}</span>
    </p>
  );
}
